<template>
  <div class="Echarts" style="padding-top: 10px">
    <div
      ref="Statistical"
      class="echarts-container"
      :style="{ width: '100%', height: chartHeight + 'px' }"
    ></div>
  </div>
</template>

<script>
// ---- 柱形几何常量 ----
// 这些值同时决定 series 的绘制和 graphic 标签的定位，必须同源：
// 只改 series 里的 barWidth/barGap 而不同步这里，标签就会偏离柱子。
const BAR_WIDTH = 30; // 单根柱子的厚度
const BAR_COUNT = 2; // 每人柱子数：brand 堆 + brand_zy 堆
const BAR_GAP_PERCENT = 40; // 同一个人的两根柱子间距（相对柱宽，40% = 12px）
const BAR_GAP_RATIO = BAR_GAP_PERCENT / 100;
const CATEGORY_GAP = 58; // 人与人之间的留白
// 一组柱子的总厚度：n×w×(1+g) − w×g（与 echarts barGrid 的 widthSum 算法一致）
const GROUP_WIDTH =
  BAR_COUNT * BAR_WIDTH * (1 + BAR_GAP_RATIO) - BAR_WIDTH * BAR_GAP_RATIO;
// 第 k 根柱子中心相对「类目带中心」的像素偏移（负值向上）
const barCenterOffset = (k) =>
  -GROUP_WIDTH / 2 + k * BAR_WIDTH * (1 + BAR_GAP_RATIO) + BAR_WIDTH / 2;
const LEFT_BAR_OFFSET = barCenterOffset(0); // 约 -21px
const RIGHT_BAR_OFFSET = barCenterOffset(1); // 约 +21px
// 每人占用的高度 = 一组柱子 + 人际留白
const PER_PERSON_HEIGHT = GROUP_WIDTH + CATEGORY_GAP;

export default {
  name: "Statistical",
  props: {
    service: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      chart: null,
      chartHeight: 600, // 横向柱状图高度：类目多时按行数自动加高，避免柱子重叠
          userTotalList: [], // 新增：存储每个用户项的 total 数据
         serTotazy_amount:[],
    userSumList: [],   // 新增：存储每个用户项的 leftSum + rightSum
      emphasisStyle: {
        itemStyle: {
          shadowBlur: 10,
          shadowColor: "rgba(0,0,0,0.3)",
        },
      },
      option: {
        legend: {
          data: [],
          left: "1%",
          top: "0%",
          formatter: (name) =>
            name.length > 5 ? name.slice(0, 5) + "..." : name,
          width: "90%",
          itemHeight: 20,
          itemGap: 15,
          textStyle: { fontSize: 12 },
        },
        tooltip: {
          trigger: "item",
          formatter: (params) => {
          
            const val = Number(params.value || 0).toFixed(2);
            return `${params.seriesName}<br/>金额：￥${val}`;
          },
        },
        grid: {
          left: "3%",
          right: "8%",
          bottom: "3%",
          top: "15%",
          containLabel: true,
        },
        // 横向柱状图：数值轴在 x，类目轴在 y
        xAxis: {
          type: "value",
          axisLine: { onZero: true },
          splitLine: { show: true, lineStyle: { type: "dashed", color: "#eee" } },
          axisLabel: { formatter: "{value}" },
        },
        yAxis: {
          type: "category",
          data: [],
          inverse: true, // 第一名置顶，保持与竖版“从左到右”一致的阅读顺序
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { interval: 0, lineHeight: 18, margin: 10 },
        },
        series: [],
        graphic: [], // 总价标签容器
        // 注意：barGap / barCategoryGap 是「系列级」配置，写在 option 根节点无效，
        // 已下沉到每个 series 中（见 obtain()）
      },
      leftTotalList: [], // 左边柱子（brand堆）总价
      rightTotalList: [], // 右边柱子（brand_zy堆）总价
      allBrandList: [], // 全局品牌列表
      chartMaxValue: 0, // 存储图表最高点数值
    };
  },
  methods: {
    // 新增：获取图表最高点（不受负数影响）
    getChartMaxValue() {
      let maxValue = 0;

      // 如果没有系列数据，返回0
      if (!this.option.series || this.option.series.length === 0) {
        return 0;
      }

      // 获取类目（客服）数量
      const categoryCount = this.option.yAxis.data.length;

      // 遍历每个类目
      for (let i = 0; i < categoryCount; i++) {
        // 计算左边柱子（main stack）的总高度
        let mainStackTotal = 0;
        // 计算右边柱子（zy stack）的总高度
        let zyStackTotal = 0;

        // 遍历所有系列，按堆叠分组计算总和
        this.option.series.forEach((series) => {
          const value = Number(series.data[i] || 0);
          // 只累加正值，忽略负数
          if (value > 0) {
            if (series.stack === "main") {
              mainStackTotal += value;
            } else if (series.stack === "zy") {
              zyStackTotal += value;
            }
          }
        });

        // 取当前类目下左右柱子的最大值
        const currentMax = Math.max(mainStackTotal, zyStackTotal);
        // 更新全局最大值
        if (currentMax > maxValue) {
          maxValue = currentMax;
        }
      }

      this.chartMaxValue = maxValue;
      console.log("图表最高点数值（不受负数影响）：", maxValue);
      return maxValue;
    },

    obtain() {
      // 重置所有数据
      this.option.legend.data = [];
      this.option.series = [];
      this.option.yAxis.data = [];
      this.leftTotalList = [];
      this.rightTotalList = [];
      this.allBrandList = [];
      this.chartMaxValue = 0;

      if (!this.service?.length) {
        this.option.graphic = [];
        this.chartHeight = 600; // 无数据时回到默认高度，避免留下大块空白
        this.renderChart();
        return;
      }

      const firstItem = this.service[0];
      this.allBrandList = firstItem.brand || [];
      const allZyList = firstItem.brand_zy || [];
      const brandTitles = this.allBrandList.map(
        (b) => b.title || `品牌${b.id || ""}`
      );

      // 1. 初始化左边柱子（brand堆）系列
      this.allBrandList.forEach((brand, idx) => {
        const name = brand.title || `品牌${idx}`;

        // 新增：提前校验该品牌是否有非0数据
        let hasNonZeroData = false;
        for (let i = 0; i < this.service.length; i++) {
          const amt = Number(this.service[i].brand?.[idx]?.amount || 0);
          if (amt > 0) {
            hasNonZeroData = true;
            break;
          }
        }

        // 只有有非0数据才添加到图例和系列中
        if (hasNonZeroData) {
          this.option.legend.data.push(name);
          this.option.series.push({
            name,
            type: "bar",
            stack: "main",
            data: [],
            emphasis: this.emphasisStyle,
            label: { show: false },
            barWidth: BAR_WIDTH,
            barGap: BAR_GAP_PERCENT + "%", // 同一个人的两根柱子间距（与标签定位共用常量）
          });
        }
      });
      // 2. 初始化右边柱子（brand_zy堆）系列
      allZyList.forEach((zyItem, idx) => {
        const baseName = brandTitles[idx] || `品牌${idx}`;
        const name = zyItem.title ? `${zyItem.title}(专)` : `${baseName}(专)`;

        // 新增：提前校验该专项品牌是否有非0数据
        let hasNonZeroData = false;
        for (let i = 0; i < this.service.length; i++) {
          const amt = Number(this.service[i].brand_zy?.[idx]?.amount || 0);
          if (amt > 0) {
            hasNonZeroData = true;
            break;
          }
        }

        // 只有有非0数据才添加到图例和系列中
        if (hasNonZeroData) {
          this.option.legend.data.push(name);
          this.option.series.push({
            name,
            type: "bar",
            stack: "zy",
            data: [],
            emphasis: this.emphasisStyle,
            label: { show: false },
            barWidth: BAR_WIDTH,
            barGap: BAR_GAP_PERCENT + "%", // 同一个人的两根柱子间距（与标签定位共用常量）
          });
        }
      });

      // 3. 填充数据 + 计算左右柱子总价
      // 注：金额为 0 的客服同样保留一行（柱长为 0），不再跳过
      this.service.forEach((userItem, dataIndex) => {
        // 计算当前用户左右柱子的总金额（brand + brand_zy）
        let leftSum = 0;
        let rightSum = 0;

        // 计算brand堆总和
        if (userItem.brand && Array.isArray(userItem.brand)) {
          leftSum = userItem.brand.reduce(
            (sum, item) => sum + Number(item.amount || 0),
            0
          );
        }

        // 计算brand_zy堆总和
        if (userItem.brand_zy && Array.isArray(userItem.brand_zy)) {
          rightSum = userItem.brand_zy.reduce(
            (sum, item) => sum + Number(item.amount || 0),
            0
          );
        }
  this.userTotalList[dataIndex] = Number(userItem.amount).toFixed(2); // 保存total
    this.serTotazy_amount[dataIndex] = Number(userItem.zy_amount).toFixed(2);
  
  this.userSumList[dataIndex] = (leftSum + rightSum).toFixed(2);     // 保存求和值
        // 计算总和并保留两位小数
        console.log(userItem.total, leftSum + rightSum);
        const totalSum = Number(userItem.total).toFixed(2);
        const label = `${userItem.username}\n￥${totalSum}`;

        this.option.yAxis.data.push(label);

        // 左边柱子（brand堆）总价计算 - 调整遍历逻辑
        let leftTotal = 0;
        let seriesIdx = 0; // 重新维护series索引
        this.allBrandList.forEach((b, idx) => {
          const amt = Number(userItem.brand?.[idx]?.amount || 0);
          // 只处理有数据的系列
          let hasNonZeroData = false;
          for (let i = 0; i < this.service.length; i++) {
            if (Number(this.service[i].brand?.[idx]?.amount || 0) > 0) {
              hasNonZeroData = true;
              break;
            }
          }

          if (hasNonZeroData) {
            leftTotal += amt;
            this.option.series[seriesIdx].data.push(amt === 0 ? null : amt);
            seriesIdx++;
          }
        });
        this.leftTotalList[dataIndex] = leftTotal;

        // 右边柱子（brand_zy堆）总价计算 - 调整遍历逻辑
        let rightTotal = 0;
        allZyList.forEach((b, idx) => {
          const amt = Number(userItem.brand_zy?.[idx]?.amount || 0);
          // 只处理有数据的系列
          let hasNonZeroData = false;
          for (let i = 0; i < this.service.length; i++) {
            if (Number(this.service[i].brand_zy?.[idx]?.amount || 0) > 0) {
              hasNonZeroData = true;
              break;
            }
          }

          if (hasNonZeroData) {
            rightTotal += amt;
            this.option.series[seriesIdx].data.push(amt === 0 ? null : amt);
            seriesIdx++;
          }
        });
        this.rightTotalList[dataIndex] = rightTotal;
      });

      // 计算图表最高点
      this.getChartMaxValue();

      // 横向柱状图按类目数量自适应高度
      // 每人高度 = 一组柱子总厚 + 人际留白（默认 72 + 58 = 130px）
      this.chartHeight = Math.max(
        600,
        this.option.yAxis.data.length * PER_PERSON_HEIGHT
      );

      this.adjustLayout();
      this.renderChart();

      // 图表渲染完成后生成标签，确保坐标系就绪
      this.$nextTick(() => {
        this.chart.resize(); // 容器高度变化后同步画布尺寸
        this.generateTotalLabels();
        this.chart.setOption(this.option);
      });
    },

    // 生成左右两个柱子的总价标签（横向：贴在柱子末端右侧，垂直居中）
    generateTotalLabels() {
      // 没有系列时 convertToPixel 的 seriesIndex 无效，直接跳过
      if (
        !this.chart ||
        this.option.yAxis.data.length === 0 ||
        this.option.series.length === 0
      ) {
        this.option.graphic = [];
        return;
      }

      const graphicList = [];
      const categoryCount = this.option.yAxis.data.length;
      // 右柱（zy 堆）的起始系列索引，兼容部分品牌被过滤掉的情况
      const zyStartIndex = this.option.series.findIndex(
        (series) => series.stack === "zy"
      );

      // 遍历每个类目（客服），生成左右柱子的标签
      for (let i = 0; i < categoryCount; i++) {
        const userTotal = this.userTotalList[i]; // 当前项的 userItem.total
        const serTotazy_amounts = this.serTotazy_amount[i];

        // convertToPixel 返回的是「类目带中心」，而一组内的两根柱子会各自上下偏移，
        // 必须再加上 barCenterOffset，否则标签会落在两根柱子中间的缝上
        const bandCenterY = this.chart.convertToPixel({ seriesIndex: 0 }, [
          0,
          i,
        ])[1];

        let leftPositiveTotal = 0;
        this.option.series.forEach((series) => {
          if (series.stack === "main" && series.data[i] !== null) {
            const val = Number(series.data[i] || 0);
            if (val > 0) {
              // 只累加正值，忽略负数
              leftPositiveTotal += val;
            }
          }
        });

        if (leftPositiveTotal > 0) {
          // 左柱：类目带中心 + 本方柱的偏移；数值轴取柱子末端 X
          const leftCenterY = bandCenterY + LEFT_BAR_OFFSET;
          const leftEndX = this.chart.convertToPixel({ seriesIndex: 0 }, [
            leftPositiveTotal,
            i,
          ])[0];

          graphicList.push({
            type: "text",
            z: 100, // 层级置顶
            position: [leftEndX + 8, leftCenterY], // 柱子末端右侧
            style: {
              text: `${Number(userTotal).toFixed(2)}`,
              fontSize: 11,
              fill: "#333",
              textAlign: "left",
              textBaseline: "middle",
              fontWeight: 500,
            },
          });
        }

        // ---------- 2. 右边柱子（brand_zy堆）总价标签 ----------
        // 重新计算右边柱子的正值总长度（不受负数影响）
        let rightPositiveTotal = 0;
        this.option.series.forEach((series) => {
          if (series.stack === "zy" && series.data[i] !== null) {
            const val = Number(series.data[i] || 0);
            if (val > 0) {
              // 只累加正值，忽略负数
              rightPositiveTotal += val;
            }
          }
        });

        if (rightPositiveTotal > 0 && zyStartIndex > -1) {
          // 右柱：类目带中心 + 本方柱的偏移；数值轴取柱子末端 X
          const rightCenterY = bandCenterY + RIGHT_BAR_OFFSET;
          const rightEndX = this.chart.convertToPixel(
            { seriesIndex: zyStartIndex },
            [rightPositiveTotal, i]
          )[0];

          graphicList.push({
            type: "text",
            z: 100,
            position: [rightEndX + 8, rightCenterY], // 柱子末端右侧
            style: {
              text: `${Number(serTotazy_amounts).toFixed(2)}`,
              fontSize: 11,
              fill: "#333",
              textAlign: "left",
              textBaseline: "middle",
              fontWeight: 500,
            },
          });
        }
      }

      this.option.graphic = graphicList;
    },
    adjustLayout() {
      const len = this.option.legend.data.length;
      if (!len || !this.$refs.Statistical) return;
      const w = this.$refs.Statistical.clientWidth * 0.9;
      const perRow = Math.max(1, Math.floor(w / 80));
      const rows = Math.ceil(len / perRow);
      const h = rows * 20 + (rows - 1) * 15 + 60;
      this.option.grid.top = `${h + 20}px`;
    },

    renderChart() {
      if (!this.chart) {
        this.chart = this.$echarts.init(this.$refs.Statistical);
        window.addEventListener("resize", this.handleResize);
      }
      this.chart.setOption(this.option, true);
    },

    handleResize() {
      if (this.chart) {
        this.chart.resize();
        this.adjustLayout();
        // 窗口缩放后重新计算最高点和标签位置
        this.$nextTick(() => {
          this.getChartMaxValue(); // 重新计算最高点
          this.generateTotalLabels();
          this.chart.setOption(this.option);
        });
      }
    },
  },
  watch: {
    service: { deep: true, handler: "obtain" },
  },
  mounted() {
    this.$nextTick(() => {
      this.chart = this.$echarts.init(this.$refs.Statistical);
      if (this.service.length) this.obtain();
      window.addEventListener("resize", this.handleResize);
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    this.chart?.dispose();
    this.chart = null;
  },
};
</script>

<style scoped>
.echarts-container {
  width: 100%;
  height: 100%;
  min-height: 500px;
}
</style>